import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Attribute } from "../../model/service-request/attribute";
import { ServiceRequestType } from "../../model/service-request/service-request";
import { Money } from "../../model/common/money";
import { SectionDefinition } from "../../model/service-request/section-definition";

const initialState: ServiceRequestType = {
	id: "",
	name: "",
	description: "",
	sectionDefinitions: [
		{
			name: "Section 1",
			displayOrder: 0,
			isDeleted: false,
			attributeDefinitions: [],
			ServiceRequestId: "",
		},
	],
	isPaymentRequired: false,
	isPaymentPerSubtype: false,
	isDeleted: false,
	isPublished: true,
	payOnSubmit: false,
};

interface AddFieldAction {
	sectionIndex: number;
	newAttribute: Attribute;
	existingAttribute?: Attribute;
}
type UpdateOptionAmountPayload = {
	sectionIndex: number;
	fieldIndex: number;
	optionIndex: number;
	amount: number;
};

interface EnableFieldAction {
	sectionIndex: number;
	attribute: Attribute;
	enabled: boolean;
}
interface RemoveFieldAction {
	sectionIndex: number;
	attribute: Attribute;
}
interface ChangeFieldOrdersAction {
	sectionIndex: number;
	attributes: Attribute[];
}
interface ChangeSectionOrdersAction {
	sections: SectionDefinition[];
}
interface RenameSectionAction {
	section: SectionDefinition;
	newName: string;
}

const serviceTypeSlice = createSlice({
	name: "serviceType",
	initialState,
	reducers: {
		setFormName: (state, action: PayloadAction<string>) => {
			state.name = action.payload;
		},
		setFormDescription: (state, action: PayloadAction<string>) => {
			state.description = action.payload;
		},
		addField: (state, action: PayloadAction<AddFieldAction>) => {
			const { newAttribute, existingAttribute, sectionIndex } = action.payload;

			if (existingAttribute) {
				const index = state.sectionDefinitions[sectionIndex].attributeDefinitions.findIndex(x => x.id === existingAttribute.id);
				if (index !== -1) {
					state.sectionDefinitions[sectionIndex].attributeDefinitions[index] = newAttribute;
				} else {
					state.sectionDefinitions[sectionIndex].attributeDefinitions.push(newAttribute);
				}
			} else {
				state.sectionDefinitions[sectionIndex].attributeDefinitions.push(newAttribute);
			}
		},
		enableField: (state, action: PayloadAction<EnableFieldAction>) => {
			const { attribute, enabled, sectionIndex } = action.payload;
			const index = state.sectionDefinitions[sectionIndex].attributeDefinitions.findIndex(x => x.id === attribute.id);

			if (index !== -1) {
				state.sectionDefinitions[sectionIndex].attributeDefinitions[index] = {
					...state.sectionDefinitions[sectionIndex].attributeDefinitions[index],
					isEnabled: enabled,
				};
			}
		},
		setIsExternal: (state, action: PayloadAction<boolean>) => {
			state.isExternal = action.payload;
		},
		setPaymentRequired: (state, action: PayloadAction<boolean>) => {
			state.isPaymentRequired = action.payload;
		},
		setIsPayOnSubmit: (state, action: PayloadAction<boolean>) => {
			state.payOnSubmit = action.payload;
		},
		setAmount: (state, action: PayloadAction<number | undefined>) => {
			state.price = action.payload ? new Money("NGN", action.payload!) : undefined;
		},
		updateOptionAmount: (state, action: PayloadAction<UpdateOptionAmountPayload>) => {
			const { fieldIndex, optionIndex, amount, sectionIndex } = action.payload;
			const field = state.sectionDefinitions[sectionIndex].attributeDefinitions[fieldIndex];
			if (!field || !field.childrenOptionDefinitions) return;

			if (field.childrenOptionDefinitions[optionIndex]) {
				field.childrenOptionDefinitions[optionIndex].price = new Money("NGN", amount);
			}
		},
		resetAllOptionAmounts: state => {
			state.sectionDefinitions.flatMap(section => {
				section.attributeDefinitions.forEach(field => {
					if (field.childrenOptionDefinitions) {
						field.childrenOptionDefinitions.forEach(opt => {
							opt.price = undefined;
						});
					}
				});
			});
		},
		setIsPaymentPerSubtype: (state, action: PayloadAction<boolean>) => {
			state.isPaymentPerSubtype = action.payload;
		},
		removeField: (state, action: PayloadAction<RemoveFieldAction>) => {
			let attrIndex = state.sectionDefinitions[action.payload.sectionIndex].attributeDefinitions.findIndex(x => x.id == action.payload.attribute.id);
			state.sectionDefinitions[action.payload.sectionIndex].attributeDefinitions.splice(attrIndex, 1);
		},
		setServiceRequestType: (state, action: PayloadAction<ServiceRequestType>) => {
			Object.assign(state, action.payload);
			if (action.payload.price != undefined) {
				state.isPaymentRequired = true;
				return;
			}

			action.payload.sectionDefinitions.forEach(x =>
				x.attributeDefinitions.forEach(attr =>
					attr.childrenOptionDefinitions?.forEach(child => {
						if (child.price) {
							state.isPaymentRequired = true;
							state.isPaymentPerSubtype = true;
						}
					})
				)
			);
		},
		resetState: state => {
			Object.assign(state, initialState);
		},
		changeOrderOfAttributes: (state, action: PayloadAction<ChangeFieldOrdersAction>) => {
			const { attributes, sectionIndex } = action.payload;
			state.sectionDefinitions[sectionIndex].attributeDefinitions = attributes;
		},
		changeOrderOfSections: (state, action: PayloadAction<ChangeSectionOrdersAction>) => {
			const { sections } = action.payload;
			state.sectionDefinitions = sections;
		},
		addNewSection: (state, action: PayloadAction<string>) => {
			state.sectionDefinitions = [
				...state.sectionDefinitions,
				{
					name: action.payload,
					isDeleted: false,
					attributeDefinitions: [],
					ServiceRequestId: state.id,
				},
			];
		},
		renameSection: (state, action: PayloadAction<RenameSectionAction>) => {
			var index = state.sectionDefinitions.findIndex(x => x.id == action.payload.section.id);
			state.sectionDefinitions[index] = { ...state.sectionDefinitions[index], name: action.payload.newName };
		},
		removeSection: (state, action: PayloadAction<SectionDefinition>) => {
			var index = state.sectionDefinitions.findIndex(x => x.id == action.payload.id);
			state.sectionDefinitions.splice(index, 1);
		},
	},
});

export const {
	setFormName,
	setFormDescription,
	addField,
	enableField,
	setIsExternal,
	setPaymentRequired,
	setIsPayOnSubmit,
	setAmount,
	updateOptionAmount,
	setIsPaymentPerSubtype,
	resetAllOptionAmounts,
	removeField,
	setServiceRequestType,
	resetState,
	changeOrderOfAttributes,
	addNewSection,
	changeOrderOfSections,
	renameSection,
	removeSection,
} = serviceTypeSlice.actions;

export default serviceTypeSlice.reducer;
